import type { ErrorEvent } from "@sentry/react";

const DENYLIST = [
  "name", // covers firstname/lastname/fullname via segment match
  "email",
  "dob",
  "birth",
  "address",
  "phone",
  "ssn",
  "mrn",
  "patient",
  "password",
  "token",
  "authorization",
  "username" // event.user.username — login handle / often the email
];

// Diagnostic keys that collide with a denylist segment but carry no PII —
// e.g. `hasAuthToken`/`hasRefreshToken` are booleans (a token *exists*), not the
// token itself, and are useless if redacted. Compared lower-cased.
const ALLOWLIST = new Set(["hasauthtoken", "hasrefreshtoken"]);

const MAX_STRING_LENGTH = 2048;
const MAX_DEPTH = 6;

/**
 * Split a key into lowercase word segments on camelCase boundaries and on
 * `_ - . / whitespace`. Matching is done per-segment so that `filename` does
 * NOT trigger the `name` denylist term, while `firstName` still does (segments:
 * ["first", "name"]).
 */
function keySegments(key: string): string[] {
  // Insert a boundary before each uppercase letter, then split on non-alphanumeric chars
  return key
    .replace(/([A-Z])/g, "_$1")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

function isDenylisted(key: string): boolean {
  const lower = key.toLowerCase();
  if (ALLOWLIST.has(lower)) {
    return false;
  }
  const segments = keySegments(key);
  return DENYLIST.some((term) => segments.includes(term));
}

/**
 * Dispatch a single value to the appropriate walk function. Keeps array and
 * object branches symmetric so nested arrays-of-arrays recurse correctly.
 */
function walkValue(
  val: unknown,
  visited: WeakSet<object>,
  depth: number
): void {
  if (Array.isArray(val)) {
    walkAndRedactArray(val, visited, depth);
  } else if (val !== null && typeof val === "object") {
    walkAndRedact(val as Record<string, unknown>, visited, depth);
  }
}

function walkAndRedactArray(
  arr: unknown[],
  visited: WeakSet<object>,
  depth: number
): void {
  if (depth > MAX_DEPTH) {
    // Fail closed — redact every object/array element in place
    for (let i = 0; i < arr.length; i++) {
      if (arr[i] !== null && typeof arr[i] === "object") {
        arr[i] = "[redacted]";
      }
    }
    return;
  }
  for (const item of arr) {
    walkValue(item, visited, depth + 1);
  }
}

function walkAndRedact(
  obj: Record<string, unknown>,
  visited: WeakSet<object>,
  depth: number
): void {
  if (depth > MAX_DEPTH) {
    // Fail closed — redact every own-enumerable value rather than leaving PHI intact
    for (const key of Object.keys(obj)) {
      obj[key] = "[redacted]";
    }
    return;
  }
  if (visited.has(obj)) {
    return;
  }
  visited.add(obj);

  for (const key of Object.keys(obj)) {
    if (isDenylisted(key)) {
      obj[key] = "[redacted]";
    } else {
      const val = obj[key];
      if (typeof val === "string" && val.length > MAX_STRING_LENGTH) {
        obj[key] = val.slice(0, MAX_STRING_LENGTH) + "…[truncated]";
      } else {
        walkValue(val, visited, depth + 1);
      }
    }
  }
}

/**
 * Sentry `beforeSend` hook that scrubs PHI/PII keys and truncates oversized
 * string payloads before events leave the browser. Apply to `event.extra`,
 * `event.contexts`, `event.request.data`, `event.user`, and each breadcrumb's
 * `data`.
 *
 * Never throws — on any unexpected error the original event is returned
 * unchanged so Sentry still receives the event.
 */
export const scrubSensitiveData = (event: ErrorEvent): ErrorEvent => {
  try {
    const visited = new WeakSet<object>();

    if (event.extra && typeof event.extra === "object") {
      walkAndRedact(event.extra as Record<string, unknown>, visited, 0);
    }

    if (event.contexts && typeof event.contexts === "object") {
      walkAndRedact(event.contexts as Record<string, unknown>, visited, 0);
    }

    if (
      event.request?.data !== undefined &&
      event.request.data !== null &&
      typeof event.request.data === "object"
    ) {
      walkAndRedact(event.request.data as Record<string, unknown>, visited, 0);
    }

    if (Array.isArray(event.breadcrumbs)) {
      for (const crumb of event.breadcrumbs) {
        if (
          crumb?.data !== undefined &&
          crumb.data !== null &&
          typeof crumb.data === "object"
        ) {
          walkAndRedact(crumb.data as Record<string, unknown>, visited, 0);
        }
      }
    }

    // FIX 5: scrub event.user — standard Sentry PII carrier (email, username, etc.)
    if (event.user !== null && typeof event.user === "object") {
      walkAndRedact(event.user as Record<string, unknown>, visited, 0);
    }

    return event;
  } catch {
    // FIX 1: never throw from a beforeSend hook — return event unchanged so
    // Sentry still receives it rather than dropping the event entirely.
    return event;
  }
};
